/**
 * Authentication Configuration
 * Supports Email/Password and Azure AD SSO
 */

import { Amplify } from 'aws-amplify';
import { getAmplifyConfig, cognitoDomain } from './amplifyConfig';

// Determine redirect URLs based on current origin
const getCurrentRedirectUrl = (): string => {
  if (typeof window === 'undefined') return 'http://localhost:3000/';
  const origin = window.location.origin;
  // Return origin with trailing slash
  return origin.endsWith('/') ? origin : origin + '/';
};

// OAuth configuration for Azure AD SSO
export const oauthConfig = {
  domain: cognitoDomain,
  scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'] as const,
  redirectSignIn: getCurrentRedirectUrl(),
  redirectSignOut: getCurrentRedirectUrl(),
  responseType: 'code' as const
};

// Configure Amplify with OAuth
export const configureAuth = () => {
  const baseConfig = getAmplifyConfig();
  
  console.log('[Auth] Current origin:', typeof window !== 'undefined' ? window.location.origin : 'N/A');
  console.log('[Auth] Redirect URL:', oauthConfig.redirectSignIn);
  
  // Only add OAuth config if Cognito domain is configured and Auth.Cognito exists
  if (oauthConfig.domain && baseConfig.Auth?.Cognito) {
    const cognitoConfig = baseConfig.Auth.Cognito;
    
    // Build config with OAuth - preserve all other configs (API, Storage, identityPoolId, etc.)
    const configWithOAuth = {
      ...baseConfig,
      Auth: {
        Cognito: {
          userPoolId: cognitoConfig.userPoolId,
          userPoolClientId: cognitoConfig.userPoolClientId,
          identityPoolId: cognitoConfig.identityPoolId, // Preserve Identity Pool ID
          loginWith: {
            ...(cognitoConfig.loginWith || {}),
            oauth: {
              domain: oauthConfig.domain,
              scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'] as any,
              redirectSignIn: [oauthConfig.redirectSignIn],
              redirectSignOut: [oauthConfig.redirectSignOut],
              responseType: 'code' as const,
              providers: [{ custom: 'AzureAD' }] as const
            }
          }
        }
      },
      // Ensure Storage config is preserved
      Storage: baseConfig.Storage
    };
    
    Amplify.configure(configWithOAuth as any);
    console.log('[Auth] OAuth config applied:', {
      domain: oauthConfig.domain,
      redirectSignIn: oauthConfig.redirectSignIn,
      redirectSignOut: oauthConfig.redirectSignOut
    });
  } else {
    Amplify.configure(baseConfig);
  }
  
  console.log('[Auth] Configured with OAuth:', oauthConfig.domain ? 'SSO Enabled' : 'SSO Disabled');
  console.log('[Auth] Storage configured:', !!baseConfig.Storage);
  console.log('[Auth] Identity Pool configured:', !!baseConfig.Auth?.Cognito?.identityPoolId);
};

// Identity provider names
export const IdentityProviders = {
  COGNITO: 'COGNITO',
  AZURE_AD: 'AzureAD'
} as const;

export type IdentityProvider = typeof IdentityProviders[keyof typeof IdentityProviders];
